import { initialPagination, initialState, makeListReducers } from '@/models/utils';
import { filtersConfig } from './utils';
import { bindServices } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';
import { getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import _ from 'lodash';

const api = bindServices('delegations');
const namespace = 'delegations';
const { BulkActionType } = CoreEnums;

const fetchRoute = 'delegation';

const DelegationsModel = {
  namespace,
  state: {
    ...initialState,
    filtersConfig,
  },
  effects: {
    *fetchData({ payload }, { call, put, select }) {
      try {
        const pagination = yield select((state) => state[namespace].pagination);
        const pageSize = yield select((state) => state.global.pageSize);
        const res = yield call(api.getAll, {
          route: payload.route ? payload.route : `${fetchRoute}/paged`,
          params: {
            limit: pageSize,
            ...CoreQueries.delegations.paged,
            ...pagination,
            ...payload,
          },
          readPriority: true,
        });

        yield put({
          type: 'queryData',
          payload: { ...res, limit: pageSize, page: payload.page },
        });
      } catch (e) {}
    },

    *fetchExport({ payload, onComplete }, { call }) {
      const { route, params } = payload;
      try {
        const res = yield call(api.getAll, { route, params });
        onComplete(res);
      } catch (e) {}
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries.delegations?.itemDetail
            : { ...CoreQueries.delegations?.item, ...(payload?.params && { ...payload.params }) },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {}
    },

    *fetchItemAll({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getAll, {
          route: payload.route ? payload.route : fetchRoute,
          params: {
            ...CoreQueries.delegations.item,
            ...payload.params,
          },
        });
        yield put({ type: 'queryDataAll', payload: res });
      } catch (e) {}
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.create, {
          route: payload.route ? payload.route : fetchRoute,
          data: payload.data,
        });
        yield put({
          type: 'success',
          payload: { ...res, success: true },
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }

          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });

          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.update, {
          route: payload.route ? payload.route : fetchRoute,
          data: payload.data,
        });
        yield put({
          type: 'success',
          payload: { ...res, success: true },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }

          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: e.message,
            },
          });

          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdateBy({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.updateById, payload);
        yield put({
          type: 'success',
          payload: { ...res, success: true },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }

          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload, onComplete }, { put, call }) {
      try {
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null },
        });

        yield call(api.remove, {
          route: payload.route ? payload.route : fetchRoute,
          id: payload.id,
        });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        onComplete();
      } catch (error) {}
    },

    *setActive({ payload }, { call, put }) {
      try {
        yield call(api.updateById, {
          route: payload.route,
          id: `${payload.id}/${payload.isActive}`,
          method: 'post',
        });
        yield put({ type: 'queryActive', payload });
      } catch (error) {}
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *openView({ payload }, { put }) {
      yield put({ type: 'queryOpenView', payload });
    },

    *showAdditionalForm({ payload }, { put }) {
      yield put({ type: 'queryShowAdditionalForm', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({
        type: 'queryOrderBy',
        payload,
      });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({
        type: 'querySuccess',
        payload,
      });
    },

    *resetState({ payload }, { put }) {
      yield put({
        type: 'queryResetState',
        payload,
      });
    },

    *editActive({ payload, params }, { call, put }) {
      try {
        yield call(api.updateById, {
          route: fetchRoute,
          id: params,
          method: 'post',
        });
        yield put({
          type: 'editActives',
          payload,
        });
      } catch (error) {}
    },

    *bulkDeleteDelegations({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        yield call(api.bulkAction, {
          route: 'delegation',
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const currentState = yield select((state) => state.delegations);
        const globalState = yield select((state) => state.global);

        const { pageSize, isActive, globalFilters, searchCriteria } = globalState;
        const filters = getFiltersGlobalFilters(globalFilters, namespace);
        const combinedFilter = [isActive, filters].filter(Boolean).join(' and ') || null;
        const defaultOrderBy = 'number desc';

        const params = {
          ...currentState.pagination,
          page: 1,
          ...(pageSize && { limit: pageSize }),
          ...(searchCriteria && { search: searchCriteria }),
          ...(currentState.notSelectSearch && { notSelectSearch: currentState.notSelectSearch }),
          ...(combinedFilter && { filter: combinedFilter }),
          ...(currentState.orderBy !== null && {
            orderBy: currentState.orderBy || defaultOrderBy,
          }),
          ...(currentState.expand && { expand: currentState.expand }),
          ...(currentState.select && { select: currentState.select }),
          ...(currentState.searchSelect && { searchSelect: currentState.searchSelect }),
        };

        yield put({
          type: 'fetchData',
          payload: params,
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },
  },
  reducers: makeListReducers({
    initialPagination,
    initialState: {
      ...initialState,
      filtersConfig,
    },
  }),
};

export default DelegationsModel;
